import { useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NOTHING_SAID, partOf, type Said } from '../lib/feedback';
import { useMedia } from '../lib/media';
import { useChrome } from '../state/chrome';

// The panel and its styles come when the button is first pressed.
const Panel = lazy(() => import('./FeedbackPanel').then((m) => ({ default: m.FeedbackPanel })));

/** Where the panel is a sheet along the foot of the window (app.css, NARROW WINDOWS). */
const PHONE = '(max-width: 760px)';

/**
 * Feedback, on every screen: a quiet button that opens a small panel about
 * the part of wellread the reader is on, "Feedback on the map". It closes on
 * Escape, a press outside it, or a move to another page, and keeps what was
 * said until it is sent or the reader is somewhere else. where: the top bar
 * of the app's pages; a review session's top line. The reader has none.
 */
export function Feedback({ where }: { where: 'bar' | 'session' }) {
  const path = useRouterState({ select: (r) => r.location.pathname });
  const href = useRouterState({ select: (r) => r.location.href });
  const part = partOf(path);
  const phone = useMedia(PHONE);
  // Open on the page it was opened on: moving to another page closes it.
  const openOn = useChrome((s) => s.feedbackOn);
  const setOpenOn = useChrome((s) => s.openFeedback);
  const open = openOn === path;
  // Moving to another page closes it for good: coming back doesn't open it again.
  useEffect(() => {
    if (openOn !== null && openOn !== path) setOpenOn(null);
  }, [openOn, path, setOpenOn]);
  // What was said is kept for the part it was said about.
  const [draft, setDraft] = useState<{ on: string; said: Said }>({ on: '', said: NOTHING_SAID });
  const said = draft.on === part.title ? draft.said : NOTHING_SAID;
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  // What had the keyboard when it opened: the account menu, on a narrow window, gives it the avatar.
  const from = useRef<Element | null>(null);
  useLayoutEffect(() => {
    if (open) from.current = document.activeElement;
  }, [open]);

  // Closed, the keyboard goes back to the button, or where it opened from when the button isn't shown.
  const close = useCallback(
    (refocus: boolean) => {
      setOpenOn(null);
      if (!refocus) return;
      const back = button.current?.getClientRects().length ? button.current : from.current;
      if (back instanceof HTMLElement && back.isConnected) back.focus();
    },
    [setOpenOn],
  );

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpenOn(null);
    };
    // Escape is the panel's first: it closes it and goes no further, so a review session isn't left.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close(true);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, close, setOpenOn]);

  return (
    <div ref={box} className={`fb fb-${where}`}>
      <button
        ref={button}
        type="button"
        className={where === 'session' ? 'btn btn-quiet' : 'fb-btn'}
        title={part.title}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close(false) : setOpenOn(path))}
      >
        Feedback
      </button>
      {open ? (
        <Suspense fallback={null}>
          <Panel
            title={part.title}
            place={part.place}
            path={href}
            at={phone ? 'sheet' : 'below'}
            said={said}
            onSay={(s) => setDraft({ on: part.title, said: s })}
            onClose={close}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
